/**
 * What: The Study Builder page declaration (#4264). Open this file first for any request about its data,
 *   actions, checks or analytics events; `hooks/usePageLifecycle.ts` runs everything declared here.
 * Transport / data-flow: Pure declaration. The source is the direct-study bootstrap read
 *   (`fetchInitialBuilderState`); its shape is checked here before the builder mounts.
 */
import * as z from 'zod'
import {
  studyAuthoringStatusSchema,
  studyAuthoringDeletionReceiptSchema,
  studyBuilderWireSnapshotSchema,
} from '@testfeed/shopify-contracts'
import { definePage, type PageActionDeclaration } from '../page-declaration'
import type { FeedbackPolicy } from '../page-feedback'
import { shopifyComponentCopy } from '@testfeed/product-language'

/** A checked direct-study source. The tips and starting market come in the server's first answer to the form. */
const initialStateSchema = z.object({
  authoring: studyAuthoringStatusSchema.nullable(),
  deletion: studyAuthoringDeletionReceiptSchema.nullable(),
  // The Worker-built starting draft for a new "Create new test" visit; null for a saved study or a cleanup receipt.
  startingDraft: studyBuilderWireSnapshotSchema.nullable(),
  direct: z.literal(true),
  shopName: z.string(),
  verifiedShop: z.string(),
})
export type StudyBuilderInitialState = z.infer<typeof initialStateSchema>

export const page = definePage({
  name: 'study-builder',
  sources: {
    'initial-state': { required: true, check: initialStateSchema },
  },
  actions: {
    'choose-product': {
      interaction: { textEditing: 'block' },
      saves: false,
      feedback: {
        failure: shopifyComponentCopy.studyBuilderProductPicker.openFailed,
      },
    },
    'upload-image': {
      interaction: { textEditing: 'block' },
      saves: false,
      feedback: {
        failure: shopifyComponentCopy.shopifyApiErrors.couldNotUploadImage012,
      },
    },
    'open-guidance': {
      interaction: { textEditing: 'allow' },
      saves: false,
      feedback: {
        failure: shopifyComponentCopy.studyBuilderGuidance.unavailable,
      },
    },
    'evaluate-change': {
      interaction: { textEditing: 'block' },
      saves: false,
      feedback: {
        failure: shopifyComponentCopy.shopifyApiErrors.couldNotLoad004,
        blocksSubmission: true,
      },
    },
    'save-draft': {
      interaction: { textEditing: 'allow' },
      saves: true,
      feedback: {
        failure: shopifyComponentCopy.shopifyApiErrors.couldNotSave005,
        success: shopifyComponentCopy.studyBuilderPage.changesSaved,
      },
    },
    // Restoring the local saved/initial baseline marks the page clean; it never deletes a draft.
    discard: {
      interaction: { textEditing: 'block' },
      saves: true,
      feedback: {
        failure: shopifyComponentCopy.shopifyApiErrors.couldNotDiscardDraft014,
        success: shopifyComponentCopy.studyBuilderPage.changesDiscarded,
      },
    },
    'delete-setup': {
      interaction: { textEditing: 'block' },
      saves: false,
      feedback: {
        failure: shopifyComponentCopy.shopifyApiErrors.couldNotDeleteStudySetup017,
        location: 'deletion',
      },
    },
    'run-study': {
      // A Submit refusal shows where the merchant acted — by the Submit bar, not the page top.
      feedback: {
        failure: shopifyComponentCopy.shopifyApiErrors.couldNotPrepareRun007,
        success: shopifyComponentCopy.studyBuilderPage.studySubmitted,
        location: 'submit',
      },
      interaction: { textEditing: 'block' },
      saves: false,
    },
  } satisfies Record<string, PageActionDeclaration & { feedback: FeedbackPolicy }>,
})
